import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';

const CloudFlareUploadPage = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle/uploading/completed/error
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // 生成UUID
  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // 文件验证
  const validateFile = (file) => {
    const validTypes = [
      'audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/x-wav',
      'audio/mp4', 'audio/m4a', 'audio/ogg', 'audio/flac',
      'audio/webm', 'video/webm'
    ];

    const validExtensions = ['.mp3', '.wav', '.m4a', '.ogg', '.flac', '.webm'];
    const hasValidType = validTypes.includes(file.type);
    const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

    if (!hasValidType && !hasValidExtension) {
      throw new Error('不支持的文件类型，请选择音频文件 (MP3, WAV, M4A, OGG, FLAC, WebM)');
    }

    if (file.size > 50 * 1024 * 1024) { // 50MB限制
      throw new Error('文件大小超过50MB限制');
    }

    return true;
  };

  // 文件选择处理
  const handleFileSelect = (selectedFile) => {
    try {
      setError('');
      setUploadResult(null);
      setUploadStatus('idle');
      setUploadProgress(0);

      validateFile(selectedFile);
      setFile(selectedFile);
    } catch (err) {
      setError(err.message);
      setFile(null);
    }
  };

  // 拖拽事件处理
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, []);

  // 文件输入变化处理
  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // 点击选择文件
  const handleSelectFile = () => {
    fileInputRef.current?.click();
  };

  // 上传文件
  const handleUpload = async () => {
    if (!file) return;

    try {
      setUploadStatus('uploading');
      setUploadProgress(0);
      setError('');

      const fileId = generateUUID();
      console.log(`🚀 开始上传文件: ${file.name}, fileId: ${fileId}`);

      // 使用apiService上传文件（支持进度监听）
      const uploadPromise = apiService.uploadFile(file, fileId);

      // 模拟进度更新（由于fetch API不支持进度监听，我们使用模拟进度）
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev < 90) return prev + 10;
          return prev;
        });
      }, 200);

      const result = await uploadPromise;

      // 清除进度定时器，设置为100%
      clearInterval(progressInterval);
      setUploadProgress(100);

      if (result.success) {
        setUploadResult({
          fileId: result.fileId,
          fileName: result.fileName,
          fileSize: result.fileSize
        });
        setUploadStatus('completed');
        console.log(`✅ 文件上传成功: ${result.fileName}`);
      } else {
        throw new Error(result.error || '上传失败');
      }

    } catch (err) {
      setError(err.message);
      setUploadStatus('error');
      console.error('上传失败:', err);
    }
  };

  // 下载文件
  const handleDownload = async () => {
    if (!uploadResult) return;

    try {
      console.log(`🚀 开始下载文件: ${uploadResult.fileId}`);
      await apiService.downloadFile(uploadResult.fileId);
      console.log(`✅ 文件下载完成: ${uploadResult.fileName}`);
    } catch (err) {
      setError(err.message);
      console.error('下载失败:', err);
    }
  };

  // 重置状态
  const handleReset = () => {
    setFile(null);
    setUploadStatus('idle');
    setUploadProgress(0);
    setUploadResult(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 返回首页
  const handleGoBack = () => {
    navigate('/');
  };

  // 格式化文件大小
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div
      className={`min-h-screen bg-black transition-colors duration-200 ${isDragOver ? 'bg-gray-900' : ''}`}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="flex flex-col items-center justify-center text-white min-h-screen px-8 py-8">
        {/* 标题区域 */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4">CloudFlare 文件上传</h1>
          <p className="text-gray-400">支持最大50MB的音频文件，直接上传到R2存储</p>
          <button
            onClick={handleGoBack}
            className="mt-4 px-6 py-2 bg-gray-700 rounded-full text-white font-medium hover:bg-gray-600 transition-colors"
          >
            返回首页
          </button>
        </div>

        {/* 错误提示区域 */}
        {error && (
          <div className="w-full max-w-md mb-6 p-4 bg-red-900 bg-opacity-50 border border-red-600 rounded-lg">
            <div className="text-red-200 text-center">{error}</div>
          </div>
        )}

        {/* 主要内容区域 */}
        <div className="w-full max-w-md">
          {uploadStatus === 'idle' && !file && (
            // 初始状态：拖拽区域
            <div className="border-2 border-dashed border-gray-600 rounded-lg p-12 text-center">
              {/* 上传图标 */}
              <div className="mb-6">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto text-gray-400">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17,8 12,3 7,8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>

              {/* 提示文字 */}
              <div className="text-xl font-medium mb-2 text-white">拖拽音频文件到此处</div>
              <div className="text-gray-400 mb-6">支持 MP3、WAV、M4A、OGG、FLAC、WebM 格式</div>

              {/* 选择文件按钮 */}
              <button
                onClick={handleSelectFile}
                className="px-8 py-3 bg-blue-600 rounded-full text-white font-medium hover:bg-blue-700 transition-colors"
              >
                选择文件
              </button>

              {/* 隐藏的文件输入 */}
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*,.mp3,.wav,.m4a,.ogg,.flac,.webm"
                onChange={handleFileInputChange}
                className="hidden"
              />
            </div>
          )}

          {uploadStatus === 'idle' && file && (
            // 文件选择后状态
            <div className="bg-gray-900 rounded-lg p-6">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold mb-4">文件信息</h2>
              </div>

              {/* 文件信息 */}
              <div className="space-y-4 mb-6">
                <div>
                  <div className="text-sm text-gray-400 mb-1">文件名</div>
                  <div className="text-white break-all">{file.name}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">文件大小</div>
                  <div className="text-white">{formatFileSize(file.size)}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">文件类型</div>
                  <div className="text-white">{file.type || '未知'}</div>
                </div>
              </div>

              {/* 操作按钮 */}
              <div className="flex flex-col space-y-4">
                <button
                  onClick={handleUpload}
                  className="w-full py-3 bg-blue-600 rounded-full text-white font-medium hover:bg-blue-700 transition-colors"
                >
                  上传文件
                </button>

                <div className="flex space-x-4">
                  <button
                    onClick={handleSelectFile}
                    className="flex-1 py-3 bg-gray-700 rounded-full text-white font-medium hover:bg-gray-600 transition-colors"
                  >
                    重新选择
                  </button>
                  <button
                    onClick={handleReset}
                    className="flex-1 py-3 bg-red-600 rounded-full text-white font-medium hover:bg-red-700 transition-colors"
                  >
                    清除文件
                  </button>
                </div>
              </div>
            </div>
          )}

          {uploadStatus === 'uploading' && (
            // 上传中状态
            <div className="bg-gray-900 rounded-lg p-6">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold mb-4">正在上传</h2>
                <div className="text-gray-400">请勿关闭页面...</div>
              </div>

              {/* 进度条 */}
              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-400 mb-2">
                  <span>上传进度</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="relative h-3 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="absolute h-full bg-blue-600 transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>

              {/* 文件信息 */}
              <div className="text-center text-gray-400">
                <div>{file.name}</div>
                <div className="text-sm mt-1">{formatFileSize(file.size)}</div>
              </div>
            </div>
          )}

          {uploadStatus === 'completed' && uploadResult && (
            // 上传完成状态
            <div className="bg-gray-900 rounded-lg p-6">
              <div className="text-center mb-6">
                {/* 成功图标 */}
                <div className="mb-4">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mx-auto text-green-500">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22,4 12,14.01 9,11.01"></polyline>
                  </svg>
                </div>
                <h2 className="text-2xl font-bold mb-2 text-green-500">上传成功!</h2>
                <div className="text-gray-400">文件已成功上传到R2存储</div>
              </div>

              {/* 上传结果信息 */}
              <div className="bg-gray-800 rounded-lg p-4 mb-6">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">文件ID:</span>
                    <span className="text-white font-mono text-xs">{uploadResult.fileId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">文件名:</span>
                    <span className="text-white">{uploadResult.fileName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">文件大小:</span>
                    <span className="text-white">{formatFileSize(uploadResult.fileSize)}</span>
                  </div>
                </div>
              </div>

              {/* 操作按钮 */}
              <div className="flex flex-col space-y-4">
                <button
                  onClick={handleDownload}
                  className="w-full py-3 bg-green-600 rounded-full text-white font-medium hover:bg-green-700 transition-colors"
                >
                  下载文件
                </button>

                <div className="flex space-x-4">
                  <button
                    onClick={handleReset}
                    className="flex-1 py-3 bg-blue-600 rounded-full text-white font-medium hover:bg-blue-700 transition-colors"
                  >
                    上传新文件
                  </button>
                  <button
                    onClick={handleGoBack}
                    className="flex-1 py-3 bg-gray-700 rounded-full text-white font-medium hover:bg-gray-600 transition-colors"
                  >
                    返回首页
                  </button>
                </div>
              </div>
            </div>
          )}

          {uploadStatus === 'error' && (
            // 上传失败状态
            <div className="bg-gray-900 rounded-lg p-6">
              <div className="text-center mb-6">
                {/* 错误图标 */}
                <div className="mb-4">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mx-auto text-red-500">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="15" y1="9" x2="9" y2="15"></line>
                    <line x1="9" y1="9" x2="15" y2="15"></line>
                  </svg>
                </div>
                <h2 className="text-2xl font-bold mb-2 text-red-500">上传失败</h2>
                <div className="text-gray-400">{error || '文件上传过程中出现错误'}</div>
              </div>

              {/* 操作按钮 */}
              <div className="flex flex-col space-y-4">
                <button
                  onClick={handleUpload}
                  className="w-full py-3 bg-blue-600 rounded-full text-white font-medium hover:bg-blue-700 transition-colors"
                >
                  重试上传
                </button>

                <div className="flex space-x-4">
                  <button
                    onClick={handleSelectFile}
                    className="flex-1 py-3 bg-gray-700 rounded-full text-white font-medium hover:bg-gray-600 transition-colors"
                  >
                    重新选择文件
                  </button>
                  <button
                    onClick={handleReset}
                    className="flex-1 py-3 bg-red-600 rounded-full text-white font-medium hover:bg-red-700 transition-colors"
                  >
                    重新开始
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CloudFlareUploadPage;